// src/components/Adminpanel.jsx
import React, { useEffect, useState } from "react";
import { translateApiMessage } from "../utils/formErrors";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import toast, { Toaster } from "react-hot-toast";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Settings,
  LogOut,
  Menu,
  X,
  MessageSquareText,
  Megaphone,
  Files,
  CalendarClock,
} from "lucide-react";

import AdminDashboardSection from "./admin/AdminDashboardSection";
import AdminUsersSection from "./admin/AdminUsersSection";
import AdminAddUserSection from "./admin/AdminAddUserSection";
import AdminSettingsSection from "./admin/AdminSettingsSection";
import AdminJobsSection from "./admin/AdminJobsSection";
import AdminSyncSchedulesSection from "./admin/AdminSyncSchedulesSection";
import AdminSmsNotificationsSection from "./admin/AdminSmsNotificationsSection";
import AdminSmsCampaignSection from "./admin/AdminSmsCampaignSection";
import AdminSmsTemplatesSection from "./admin/AdminSmsTemplatesSection";

const AdminPanel = () => {
  const { section } = useParams();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(section || "dashboard");

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    role: "editor",
  });

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [ntsCreds, setNtsCreds] = useState({ username: "", password: "" });
  const [ntsEdit, setNtsEdit] = useState({ username: "", password: "" });
  // The API never returns the saved password, only whether one exists.
  const [ntsHasPassword, setNtsHasPassword] = useState(false);
  const [ntsList, setNtsList] = useState([]);

  const [totalUsers, setTotalUsers] = useState(0);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [totalAccountants, setTotalAccountants] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  const roleOptions = [
    { value: "admin", label: "مدیر" },
    { value: "editor", label: "ویرایشگر" },
    { value: "viewer", label: "مشاهده‌گر" },
    { value: "accountent", label: "حسابدار" },
    { value: "client", label: "مشتری" },
  ];

  // ---------- API calls ----------
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const res = await axiosInstance.get("/accounts/admin/users");
      const data = res.data.results || res.data || [];
      setTotalUsers(data.length);
      setTotalAdmins(data.filter((u) => u.role === "admin").length);
      setTotalAccountants(data.filter((u) => u.role === "accountent").length);
    } catch (err) {
      console.error(err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await axiosInstance.get("/accounts/admin/users");
      setUsers(res.data.results || res.data || []);
    } catch (err) {
      toast.error("خطا در دریافت کاربران");
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchNtsCreds = async () => {
    try {
      const res = await axiosInstance.get("/ntsw-creds");
      const list = res.data.results || res.data || [];
      setNtsList(list);
      const first = list[0];
      if (first) {
        setNtsEdit({ username: first.username || "", password: "" });
        setNtsHasPassword(Boolean(first.has_password));
      } else {
        setNtsEdit({ username: "", password: "" });
        setNtsHasPassword(false);
      }
    } catch (err) {
      toast.error("خطا در دریافت دسترسی سامانه جامع");
    }
  };

  useEffect(() => {
    if (section) setActiveSection(section);
  }, [section]);

  useEffect(() => {
    const target =
      activeSection === "dashboard"
        ? "/adminpanel"
        : `/adminpanel/${activeSection}`;
    if (section !== activeSection) navigate(target, { replace: true });

    if (activeSection === "dashboard") fetchStats();
    if (activeSection === "users") fetchUsers();
    if (activeSection === "settings") fetchNtsCreds();
  }, [activeSection, navigate, section]);

  // ---------- Handlers ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading("در حال ایجاد کاربر...");
    try {
      await axiosInstance.post("/accounts/admin/users/", form);
      toast.success("کاربر جدید ایجاد شد.", { id: loadingToast });
      setForm({
        username: "",
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        phone_number: "",
        role: "editor",
      });
      if (activeSection === "users") fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(translateApiMessage(err.response?.data?.detail) || "ایجاد کاربر ناموفق بود", {
        id: loadingToast,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("حذف کاربر انجام شود؟")) return;
    const loadingToast = toast.loading("در حال حذف...");
    try {
      await axiosInstance.delete(`/accounts/admin/users/${id}/`);
      toast.success("کاربر حذف شد.", { id: loadingToast });
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error("خطا در حذف کاربر", { id: loadingToast });
    }
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setEditForm({
      username: user.username,
      email: user.email,
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      phone_number: user.phone_number || "",
      role: user.role || "editor",
      is_active: user.is_active ?? true,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    const loadingToast = toast.loading("در حال ذخیره تغییرات...");
    try {
      await axiosInstance.patch(
        `/accounts/admin/users/${editingId}/`,
        editForm
      );
      toast.success("تغییرات کاربر ذخیره شد.", { id: loadingToast });
      setEditingId(null);
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(translateApiMessage(err.response?.data?.detail) || "خطا در ذخیره تغییرات کاربر", {
        id: loadingToast,
      });
    }
  };

  const handleNtsChange = (e) => {
    const { name, value } = e.target;
    setNtsCreds((prev) => ({ ...prev, [name]: value }));
  };

  const saveNtsCreds = () => {
    axiosInstance
      .post("/ntsw-creds/", {
        username: ntsCreds.username,
        password: ntsCreds.password,
      })
      .then(() => {
        toast.success("اطلاعات سامانه جامع ذخیره شد.");
        fetchNtsCreds();
      })
      .catch(() => toast.error("ثبت اطلاعات سامانه جامع ناموفق بود."));
  };

  const handleNtsEditChange = (e) => {
    const { name, value } = e.target;
    setNtsEdit((prev) => ({ ...prev, [name]: value }));
  };

  const refreshNtsCookies = () => {
    const username = ntsEdit.username || ntsCreds.username;
    const password = ntsEdit.password || ntsCreds.password;
    // The server logs in with the saved credentials; a typed password is optional.
    if (!username || !(password || ntsHasPassword)) {
      toast.error("نام کاربری و رمز سامانه جامع وارد نشده است.");
      return;
    }
    const loading = toast.loading("در حال بروزرسانی سامانه...");
    axiosInstance
      .post("/ntsw/refresh-cookies/", { username, password })
      .then(() => {
        toast.success("بروزرسانی سامانه انجام شد.", { id: loading });
      })
      .catch(() => {
        toast.error("بروزرسانی سامانه ناموفق بود.", { id: loading });
      });
  };

  const updateNtsCreds = () => {
    axiosInstance
      .post("/ntsw-creds/", {
        username: ntsEdit.username,
        password: ntsEdit.password,
      })
      .then(() => {
        toast.success("ویرایش دسترسی سامانه جامع ثبت شد.");
        fetchNtsCreds();
      })
      .catch(() => toast.error("ویرایش دسترسی سامانه جامع ناموفق بود."));
  };

  const menuItems = [
    { id: "dashboard", label: "داشبورد", icon: LayoutDashboard },
    { id: "users", label: "کاربران سیستم", icon: Users },
    { id: "add-user", label: "افزودن کاربر جدید", icon: UserPlus },
    { id: "jobs", label: "وظایف سامانه جامع", icon: Settings },
    { id: "schedules", label: "زمان‌بندی همگام‌سازی", icon: CalendarClock },
    { id: "sms", label: "پیامک اعلان‌ها", icon: MessageSquareText },
    { id: "sms-campaigns", label: "ارسال پیامک", icon: Megaphone },
    { id: "sms-templates", label: "قالب‌های پیامک", icon: Files },
    { id: "settings", label: "تنظیمات", icon: Settings },
  ];

  const currentLabel =
    menuItems.find((m) => m.id === activeSection)?.label || "داشبورد";

  const menuItemBase =
    "w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl mb-1.5 text-xs sm:text-sm transition-all";

  const handleMenuClick = (id) => {
    setActiveSection(id);
    // Close drawer on mobile
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      {/* Root is padded from top to clear the fixed main navbar (assumed h-16) */}
      <div className="bg-slate-50 min-h-screen pt-16" dir="rtl">
        {/* Admin shell: fixed height below navbar, inner scroll only */}
        <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
          {/* MOBILE overlay – only under navbar (top-16) */}
          {sidebarOpen && (
            <div
              className="fixed inset-x-0 top-16 bottom-0 bg-black/40 z-30 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar: drawer on mobile, static on desktop */}
          <aside
            className={`
              fixed top-16 bottom-0 right-0 z-30 w-64 bg-slate-950 text-slate-100 
              border-l border-slate-800/70 shadow-2xl transform transition-transform duration-300
              ${sidebarOpen ? "translate-x-0" : "translate-x-full"}
              md:static md:translate-x-0 md:flex md:flex-col md:w-64 md:shadow-none md:h-full
            `}
          >
            {/* Sidebar header */}
            <div className="px-4 py-3 sm:py-4 border-b border-slate-800/80 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center font-bold text-base shadow-sm">
                  AP
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-semibold mt-5">پنل مدیریت</span>
                  <span className="text-[0.7rem] text-slate-400">
                    مدیریت کاربران و دسترسی‌ها
                  </span>
                </div>
              </div>

              {/* Close button on mobile */}
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-slate-400 hover:text-slate-100 transition-colors md:hidden"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Menu (scrolls independently if long) */}
            <nav className="flex-1 px-3 pt-3 pb-3 md:pb-4 overflow-y-auto space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleMenuClick(item.id)}
                    className={`${menuItemBase} ${
                      active
                        ? "bg-gradient-to-l from-sky-500 to-indigo-500 text-white shadow-md shadow-sky-900/30"
                        : "text-slate-300 hover:bg-slate-800/80 hover:text-white"
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-9 h-9 rounded-xl ${
                        active
                          ? "bg-white/10"
                          : "bg-slate-900/70 border border-slate-700/70"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="whitespace-nowrap">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Footer / Logout */}
            <div className="px-3 pb-3 md:pb-4 pt-2 border-t border-slate-800/80">
              <button
                className={`${menuItemBase} text-rose-300 hover:text-rose-100 hover:bg-rose-900/40`}
                // wire to real logout if needed
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-rose-900/50 border border-rose-700/60">
                  <LogOut className="w-4 h-4" />
                </span>
                <span>خروج از پنل</span>
              </button>
            </div>
          </aside>

          {/* Main content column (fills remaining height, only this scrolls) */}
          <div className="flex-1 flex flex-col min-w-0 h-full">
            {/* Header inside admin area */}
            <header className="bg-white/90 backdrop-blur border-b border-slate-200/80 px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Mobile: menu trigger */}
                <button
                  className="md:hidden p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-start gap-1 mt-4">
                  <h1 className="text-base sm:text-xl font-bold text-slate-900">
                    {currentLabel}
                  </h1>
                  <p className="text-[0.7rem] sm:text-xs text-slate-500">
                    مدیریت متمرکز تنظیمات سیستم و کاربران
                  </p>
                </div>
              </div>

              <div className="hidden sm:flex items-center gap-2 text-[0.7rem]">
                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                  Admin Panel
                </span>
              </div>
            </header>

            {/* Scrollable content area */}
            <main className="flex-1 overflow-y-auto px-3 sm:px-6 md:px-8 py-4 sm:py-6 bg-slate-50/80">
              <div className="max-w-6xl mx-auto space-y-6">
                {activeSection === "dashboard" && (
                  <AdminDashboardSection
                    statsLoading={statsLoading}
                    totalUsers={totalUsers}
                    totalAdmins={totalAdmins}
                    totalAccountants={totalAccountants}
                  />
                )}

                {activeSection === "users" && (
                  <AdminUsersSection
                    users={users}
                    usersLoading={usersLoading}
                    editingId={editingId}
                    editForm={editForm}
                    setEditForm={setEditForm}
                    startEdit={startEdit}
                    cancelEdit={cancelEdit}
                    saveEdit={saveEdit}
                    handleDelete={handleDelete}
                    roleOptions={roleOptions}
                  />
                )}

                {activeSection === "add-user" && (
                  <AdminAddUserSection
                    form={form}
                    setForm={setForm}
                    handleSubmit={handleSubmit}
                    roleOptions={roleOptions}
                  />
                )}

                {activeSection === "jobs" && <AdminJobsSection />}

                {activeSection === "schedules" && <AdminSyncSchedulesSection />}

                {activeSection === "sms" && <AdminSmsNotificationsSection />}

                {activeSection === "sms-campaigns" && <AdminSmsCampaignSection />}

                {activeSection === "sms-templates" && <AdminSmsTemplatesSection />}

                {activeSection === "settings" && (
                  <AdminSettingsSection
                    ntsCreds={ntsCreds}
                    handleNtsChange={handleNtsChange}
                    saveNtsCreds={saveNtsCreds}
                    ntsList={ntsList}
                    ntsEdit={ntsEdit}
                    handleNtsEditChange={handleNtsEditChange}
                    updateNtsCreds={updateNtsCreds}
                    refreshNtsCookies={refreshNtsCookies}
                  />
                )}
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPanel;
